import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { StatCard } from '../../../../shared/components/StatCard.js';
import { Alert } from '../../../../shared/components/Alert.js';

/** What is owed, aged into the tenant's buckets (the Sales Accounts ledger's ageing). */
interface Ageing {
  asOn: string;
  buckets: Array<{ from: number; to: number | null; amount: number }>;
  total: number;
}

interface Tiles {
  pendingPayments?: number;
  approvedPayments?: number;
  completedPayments?: number;
  highValuePayments?: number;
  pendingAuthorizations?: number;
  paymentAging?: Ageing;
  vendorSummary?: number;
}

/** L15.01 — Payment Dashboard: where every bill stands on its way to payment, and what is owed. */
export function PaymentDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { money } = useBusinessNumber();
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [tiles, setTiles] = useState<Tiles | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiRequest<Tiles>('/v1/payments/dashboard/tiles', { token })
      .then(setTiles)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const bucketLabel = (b: { from: number; to: number | null }) =>
    b.to === null
      ? t('finance.dash.agingOver', { days: b.from - 1 })
      : t('finance.dash.agingBucket', { from: b.from, to: b.to });

  const aging = tiles?.paymentAging;

  return (
    <div className="tl-page" style={{ padding: 24 }}>
      <PageHeader title={t('finance.dash.title')} subtitle={t('finance.dash.subtitle')} />
      {error && <Alert tone="danger">{error}</Alert>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.dash.loading')}</p>}
      {tiles && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 24 }}>
            <StatCard label={t('finance.dash.pending')} value={tiles.pendingPayments ?? 0} />
            <StatCard label={t('finance.dash.pendingAuth')} value={tiles.pendingAuthorizations ?? 0} />
            <StatCard label={t('finance.dash.approved')} value={tiles.approvedPayments ?? 0} />
            <StatCard label={t('finance.dash.completed')} value={tiles.completedPayments ?? 0} />
            <StatCard label={t('finance.dash.highValue')} value={tiles.highValuePayments ?? 0} />
            <StatCard label={t('finance.dash.vendors')} value={tiles.vendorSummary ?? 0} />
          </div>
          {aging && (
            <Card title={t('finance.dash.aging')} actions={(
              <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
                {t('finance.dash.agingAsOn', { date: day(aging.asOn), total: money(aging.total) })}
              </span>
            )}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${aging.buckets.length}, minmax(120px, 1fr))`, gap: 12 }}>
                {aging.buckets.map((b) => (
                  <div key={b.from} data-testid={`aging-${b.from}`}
                    style={{ padding: 16, borderRadius: 10, background: 'var(--color-bg-muted)', border: '1px solid var(--color-border-default)' }}>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>₹{money(b.amount)}</div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 4 }}>{bucketLabel(b)}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
      <nav aria-label={t('finance.dash.title')} className="tl-stack tl-stack--row" style={{ gap: 20, marginTop: 24 }}>
        <Link to="/finance/maker/queue">{t('finance.dash.linkMaker')}</Link>
        <Link to="/finance/payment-entry">{t('finance.dash.linkPaymentEntry')}</Link>
        <Link to="/finance/reconciliation">{t('finance.dash.linkRecon')}</Link>
        <Link to="/finance/holds">{t('finance.dash.linkHolds')}</Link>
        <Link to="/finance/gst">{t('finance.dash.linkGst')}</Link>
        <Link to="/finance/tracking">{t('finance.dash.linkTracking')}</Link>
        <Link to="/finance/advice">{t('finance.dash.linkAdvice')}</Link>
      </nav>
    </div>
  );
}
